import { Fragment, useState } from "react";
import { IconChevronDown, IconPlus, IconReceipt, IconTrash } from "@tabler/icons-react";
import { Button, Group, IconAction, Input, NumberInput, Stack, Table, Tbody, Td, Text, Th, Thead, Tr } from "@/components/ui";
import type { ConceptoCompraInput, ImpuestoCompraInput } from "@/lib/types";

export function conceptoVacio(): ConceptoCompraInput {
  return {
    lineaPedido: "",
    cantidad: 1,
    unidad: null,
    noIdentificacion: null,
    descripcion: "",
    valorUnitario: 0,
    importe: 0,
    observaciones: null,
    centroCostos: null,
    descCecos: null,
    cuentaContable: null,
    impuestos: [],
  };
}

function impuestoVacio(): ImpuestoCompraInput {
  return { tipo: "IVA", indicador: null, monto: 0, porcentaje: 16 };
}

interface Props {
  value: ConceptoCompraInput[];
  onChange: (v: ConceptoCompraInput[]) => void;
  /** Los conceptos de entradas de almacén no llevan CeCo/cuenta ni indicador de impuesto. */
  simple?: boolean;
  /** Solo lectura · los conceptos provienen del XML y no se editan en el portal. */
  readOnly?: boolean;
}

/** Resumen legible de los impuestos de un concepto (ej. "IVA 16%"). */
function resumenImpuestos(impuestos: ImpuestoCompraInput[]): string {
  if (impuestos.length === 0) return "—";
  return impuestos.map((x) => `${x.tipo} ${x.porcentaje}%`).join(" · ");
}

/**
 * Editor controlado de conceptos (con impuestos anidados) · tabla con edición
 * inline por celda, agregar/eliminar filas, y un panel colapsable de impuestos
 * por concepto. Lo usan los forms de OC y CA. En modo `readOnly` los conceptos
 * (que vienen del XML) se muestran como tabla de solo lectura.
 */
export function ConceptosEditor({ value, onChange, simple = false, readOnly = false }: Props) {
  const [abierto, setAbierto] = useState<number | null>(null);

  const upd = (i: number, patch: Partial<ConceptoCompraInput>) =>
    onChange(value.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  const del = (i: number) => onChange(value.filter((_, idx) => idx !== i));
  const add = () => onChange([...value, conceptoVacio()]);

  const updImpuestos = (i: number, imps: ImpuestoCompraInput[]) => upd(i, { impuestos: imps });

  if (readOnly) {
    return (
      <Stack gap="xs">
        <Text size="sm" fw={600}>Conceptos ({value.length})</Text>
        {value.length === 0 ? (
          <Text size="xs" c="dimmed">Sin conceptos.</Text>
        ) : (
          <div className="overflow-x-auto">
            <Table verticalSpacing="xs" withRowBorders>
              <Thead>
                <Tr>
                  <Th>Línea</Th>
                  <Th>Descripción</Th>
                  <Th align="right">Cantidad</Th>
                  <Th>Unidad</Th>
                  <Th align="right">V. unitario</Th>
                  <Th align="right">Importe</Th>
                  {!simple && <Th>Cuenta</Th>}
                  {!simple && <Th>CeCo</Th>}
                  <Th>Impuestos</Th>
                </Tr>
              </Thead>
              <Tbody>
                {value.map((c, i) => (
                  <Tr key={i}>
                    <Td className="font-mono">{c.lineaPedido || "—"}</Td>
                    <Td>{c.descripcion || "—"}</Td>
                    <Td align="right">{c.cantidad}</Td>
                    <Td>{c.unidad ?? "—"}</Td>
                    <Td align="right">{c.valorUnitario}</Td>
                    <Td align="right">{c.importe}</Td>
                    {!simple && <Td>{c.cuentaContable ?? "—"}</Td>}
                    {!simple && <Td>{c.centroCostos ?? "—"}</Td>}
                    <Td>{resumenImpuestos(c.impuestos)}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </div>
        )}
      </Stack>
    );
  }

  return (
    <Stack gap="xs">
      <Group justify="space-between">
        <Text size="sm" fw={600}>Conceptos ({value.length})</Text>
        <Button size="xs" variant="light" leftSection={<IconPlus size={14} />} onClick={add}>
          Agregar concepto
        </Button>
      </Group>

      {value.length === 0 ? (
        <Text size="xs" c="dimmed">Sin conceptos · agrega al menos uno.</Text>
      ) : (
        <div className="overflow-x-auto">
          <Table verticalSpacing="xs" withRowBorders>
            <Thead>
              <Tr>
                <Th className="w-22">Línea</Th>
                <Th className="min-w-50">Descripción</Th>
                <Th className="w-22">Cantidad</Th>
                <Th className="w-20">Unidad</Th>
                <Th className="w-30">V. unitario</Th>
                <Th className="w-30">Importe</Th>
                {!simple && <Th className="w-28">Cuenta</Th>}
                {!simple && <Th className="w-28">CeCo</Th>}
                <Th className="w-20" />
              </Tr>
            </Thead>
            <Tbody>
              {value.map((c, i) => (
                <Fragment key={i}>
                  <Tr>
                    <Td>
                      <Input size="xs" value={c.lineaPedido} onChange={(e) => upd(i, { lineaPedido: e.currentTarget.value })} />
                    </Td>
                    <Td>
                      <Input size="xs" value={c.descripcion} onChange={(e) => upd(i, { descripcion: e.currentTarget.value })} />
                    </Td>
                    <Td>
                      <NumberInput size="xs" hideControls value={c.cantidad} onChange={(v) => upd(i, { cantidad: Number(v) || 0 })} />
                    </Td>
                    <Td>
                      <Input size="xs" value={c.unidad ?? ""} onChange={(e) => upd(i, { unidad: e.currentTarget.value || null })} />
                    </Td>
                    <Td>
                      <NumberInput size="xs" hideControls decimals={4} value={c.valorUnitario} onChange={(v) => upd(i, { valorUnitario: Number(v) || 0 })} />
                    </Td>
                    <Td>
                      <NumberInput size="xs" hideControls decimals={4} value={c.importe} onChange={(v) => upd(i, { importe: Number(v) || 0 })} />
                    </Td>
                    {!simple && (
                      <Td>
                        <Input size="xs" value={c.cuentaContable ?? ""} onChange={(e) => upd(i, { cuentaContable: e.currentTarget.value || null })} />
                      </Td>
                    )}
                    {!simple && (
                      <Td>
                        <Input size="xs" value={c.centroCostos ?? ""} onChange={(e) => upd(i, { centroCostos: e.currentTarget.value || null })} />
                      </Td>
                    )}
                    <Td>
                      <Group gap={2} className="flex-nowrap justify-end">
                        <IconAction label="Impuestos" size="xs" onClick={() => setAbierto(abierto === i ? null : i)}>
                          <IconReceipt size={14} />
                        </IconAction>
                        <IconAction label="Eliminar concepto" size="xs" iconColor="peligro-600" onClick={() => del(i)}>
                          <IconTrash size={14} />
                        </IconAction>
                      </Group>
                    </Td>
                  </Tr>
                  {abierto === i && (
                    <Tr>
                      <Td colSpan={simple ? 7 : 9} className="border-0 p-0">
                        <ImpuestosEditor
                          value={c.impuestos}
                          onChange={(imps) => updImpuestos(i, imps)}
                          simple={simple}
                        />
                      </Td>
                    </Tr>
                  )}
                </Fragment>
              ))}
            </Tbody>
          </Table>
        </div>
      )}
    </Stack>
  );
}

function ImpuestosEditor({
  value,
  onChange,
  simple,
}: {
  value: ImpuestoCompraInput[];
  onChange: (v: ImpuestoCompraInput[]) => void;
  simple: boolean;
}) {
  const upd = (i: number, patch: Partial<ImpuestoCompraInput>) =>
    onChange(value.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  const del = (i: number) => onChange(value.filter((_, idx) => idx !== i));
  const add = () => onChange([...value, impuestoVacio()]);

  return (
    <Stack gap={4} className="bg-muted/40 p-2">
      <Group justify="space-between">
        <Group gap={6}>
          <IconChevronDown size={12} />
          <Text size="xs" fw={600}>Impuestos ({value.length})</Text>
        </Group>
        <Button size="xs" variant="subtle" leftSection={<IconPlus size={12} />} onClick={add}>
          Agregar impuesto
        </Button>
      </Group>
      {value.map((x, i) => (
        <Group key={i} gap={6} className="flex-nowrap">
          <Input size="xs" placeholder="Tipo" wrapperClassName="w-22" value={x.tipo} onChange={(e) => upd(i, { tipo: e.currentTarget.value })} />
          {!simple && (
            <Input size="xs" placeholder="Indicador" wrapperClassName="w-22" value={x.indicador ?? ""} onChange={(e) => upd(i, { indicador: e.currentTarget.value || null })} />
          )}
          <NumberInput size="xs" hideControls placeholder="Monto" wrapperClassName="w-28" decimals={4} value={x.monto} onChange={(v) => upd(i, { monto: Number(v) || 0 })} />
          <NumberInput size="xs" hideControls placeholder="%" wrapperClassName="w-20" decimals={4} value={x.porcentaje} onChange={(v) => upd(i, { porcentaje: Number(v) || 0 })} />
          <IconAction label="Eliminar impuesto" size="xs" iconColor="peligro-600" onClick={() => del(i)}>
            <IconTrash size={13} />
          </IconAction>
        </Group>
      ))}
    </Stack>
  );
}
